const _ = require('lodash');
const customMetricHelpers = require('./customMetric');
const newCustomMetricHelpers = require('./customMetricNew');
const resultIndicatorUtils = require('./utils/resultIndicatorUtils');
const { getRemainingDays, getTotalDurationInDays, getNumberOfDaysPerRow, getNumberOfDaysInMonth } = require('./utils');

const moment = require('moment');
const SMALLER_VALUE = 0.000001;

function getRowId(row) {
  return [row.id, row.from, row.to].join('-');
}

function mergeHeaders(headers) {
  const mergedHeader = {};
  for (const header of headers) {
    for (const field of header) {
      if (!mergedHeader[field.name]) {
        mergedHeader[field.name] = field;
      } else {
        const oldField = mergedHeader[field.name];
        if (oldField.aggregation?.type === 'auto') {
          mergedHeader[field.name] = field;
        }
      }
    }
  }
  return Object.values(mergedHeader);
  // return _.uniqBy(headers.flat(), h=>h.name)
}

function unionTables({ tables, customMetrics, originalDateFilter = undefined }) {
  const table = {};
  table.headers = mergeHeaders(tables.map(t => t.headers));
  const tableWarnings = [];
  for (const table of tables) {
    if (table.warnings) {
      tableWarnings.push(...table.warnings);
    }
  }
  table.warnings = tableWarnings;
  table.rows = tables.flatMap(t => t.rows);
  table.refreshTime = _.min(tables.map(table => table.refreshTime).filter(Boolean));
  table.dimension = tables?.[0]?.dimension;
  table.groupBy = tables?.[0]?.groupBy;
  const aggregatedTable = aggregateTable({ table, customMetrics, originalDateFilter });
  return aggregatedTable;
}

function aggregateTable({ table, customMetrics, options, originalDateFilter }) {
  if (!options) {
    options = {
      groupByFunction: row => [row.id, row.from, row.to].join('-'),
    };
  }
  const channel = table?.rows?.[0]?.channel;
  const resultIndicator = resultIndicatorUtils.getResultIndicator(table.rows);
  // TODO: This function needs to be fast. So no fat arrow function, no lodash.
  const rowsById = _.groupBy(table.rows, options.groupByFunction);

  const isUsingCustomMetricV2 = !!customMetrics?.[0]?.details;
  const beforeCalculatedCustomMetrics = customMetrics.filter(
    customMetric => isUsingCustomMetricV2 && customMetric.details.length > 1,
  );
  const isBeforeCalculatedCustomMetricExist = beforeCalculatedCustomMetrics.length > 0;
  const customMetricsByChannel = {};
  let aggregatedRows = [];
  for (const rows of Object.values(rowsById)) {
    if (rows.length === 0) {
      continue;
    }

    const cells = {};

    for (const row of rows) {
      // ? it will skep the {row.name === 'all'}, Because all data is already.
      // ? But I also set the condition {row.id === 'others'} because campaign name can be all...
      if (row.id === 'others' && row.name === 'all') continue;
      const channel = row.channel;
      // used for conditional custom metric + blend data https://adriel-chat.slack.com/archives/CRUJNDLGN/p1689761132700269?thread_ts=1689582377.913449&cid=CRUJNDLGN
      const customMetricChannel = row.channel ?? row.originalChannel;
      if (!customMetricsByChannel[customMetricChannel]) {
        const formattedCustomMetric = newCustomMetricHelpers.getOldFormatCustomMetricsByChannel(
          beforeCalculatedCustomMetrics,
          customMetricChannel,
        );
        const sortedCustomMetric = customMetricHelpers.sortCustomMetrics(formattedCustomMetric);
        customMetricsByChannel[customMetricChannel] = sortedCustomMetric;
      }
      const sortedCustomMetric = customMetricsByChannel[customMetricChannel];
      customMetricHelpers.addCustomMetrics({
        cells: row.cells,
        customMetrics: sortedCustomMetric,
        resultIndicator,
      });

      cells['numberOfDaysPerRow'] = getNumberOfDaysPerRow(row.from, row.to, originalDateFilter?.timeZone);
      cells['remainingDays'] = getRemainingDays(row.to, originalDateFilter?.timeZone);
      cells['totalDurationInDays'] = getTotalDurationInDays(row.from, row.to);
    }

    if (originalDateFilter?.from && originalDateFilter?.to) {
      cells['remainingDays'] = getRemainingDays(originalDateFilter.to, originalDateFilter?.timeZone);
      cells['totalDurationInDays'] = getTotalDurationInDays(originalDateFilter.from, originalDateFilter.to);
    }

    cells['numberOfDaysInMonth'] = originalDateFilter?.isMonthToDate
      ? getNumberOfDaysInMonth(originalDateFilter.to)
      : 30.4;

    for (const header of table.headers) {
      const fieldName = header.name;
      let aggregation = header.aggregation;
      if (!aggregation || aggregation.type == 'auto') {
        const values = rows.map(row => row.cells[fieldName]).filter(val => val != undefined);
        if (values.length == 0) {
          continue;
        }
        const valueTypes = values.map(value => typeof value);
        if (valueTypes.length == 0) {
          cells[fieldName] = null;
          continue;
        } else if (valueTypes.every(type => type == 'number')) {
          aggregation = { type: 'sum' };
          // } else if (valueTypes.every(type => type == 'string')) {
          //   const maxCount = fieldName.startsWith('utm') ? null : 10;
          //   aggregation = { type: 'set', maxCount };
        } else {
          aggregation = { type: 'unique' };
        }
      }
      if (aggregation.type == 'unique') {
        let isUnique = true;
        let pickedRow = rows[0];
        let val = pickedRow.cells[fieldName];
        for (let r = 1; r < rows.length; r++) {
          if (val === undefined || val === null) {
            // if val is null or undefined
            val = rows[r].cells[fieldName];
            if (!val != null) {
              pickedRow = rows[r];
            }
          } else {
            if (rows[r].cells[fieldName] && rows[r].cells[fieldName] !== val) {
              if (pickedRow.dataSourceType === rows[r].dataSourceType) {
                isUnique = false;
              } else if (rows[r].dataSourceType === 'marketing') {
                val = rows[r].cells[fieldName];
              }
              break;
            }
          }
        }
        if (isUnique) {
          cells[fieldName] = val;
        }
      } else if (aggregation.type == 'sum') {
        let sum = null;
        for (const row of rows) {
          const rowVal = row.cells[fieldName];
          if (rowVal != null) {
            if (sum == null) sum = 0;
            sum += rowVal;
          }
        }
        if (sum != null) {
          sum = sum > SMALLER_VALUE ? sum : 0;
        }
        cells[fieldName] = sum;
      } else if (aggregation.type == 'set') {
        cells[fieldName] = _.uniq(rows.flatMap(row => row.cells[fieldName]).filter(Boolean));
        // only in this function for the widget, it shouldn't be in the query result aggregateCell function
        cells[fieldName] = cells[fieldName].slice(0, Math.min(aggregation.maxCount ?? 10, 10));
      } else if (aggregation.type == 'custom' && aggregation?.args?.type == 'budget') {
        const budget = {
          lifeTime: 0,
          daily: 0,
          isLifeTimeBudget: false,
          sharedBudgetId: undefined,
          sharedBudgetIds: [],
        };
        for (const row of rows) {
          if (row.cells.lifeTimeBudgetAmount || row.cells.dailyBudgetAmount) {
            if (row.cells.sharedBudgetId) {
              if (budget.sharedBudgetIds.includes(row.cells.sharedBudgetId)) {
                continue;
              } else {
                budget.sharedBudgetIds.push(row.cells.sharedBudgetId);
              }
            }
            if (row.cells?.budget?.['sharedBudgetIds']) {
              budget.sharedBudgetIds.push(...row.cells.budget['sharedBudgetIds']);
            }
            budget.lifeTime += row.cells.lifeTimeBudgetAmount || 0;
            budget.daily += row.cells.dailyBudgetAmount || 0;
          }
          if (budget.lifeTime && !budget.daily) {
            budget.isLifeTimeBudget = true;
          }
        }
        if (budget.sharedBudgetIds.length === 1) {
          budget.sharedBudgetId = budget.sharedBudgetIds[0];
        }
        cells[fieldName] = budget;
      } else if (aggregation.type == 'custom' && aggregation?.args?.type == 'resultIndicators') {
        const resultIndicatorsSet = new Set();
        for (const row of rows) {
          const rowVals = row.cells[fieldName];
          if (rowVals) {
            for (const rowVal of rowVals) {
              resultIndicatorsSet.add(rowVal);
            }
          }
        }
        cells[fieldName] = Array.from(resultIndicatorsSet);
      } else if (aggregation.type == 'custom' && aggregation?.args?.type == 'results') {
        const results = {};
        for (const row of rows) {
          if (!row.cells.resultIndicators) {
            continue;
          }
          if (!Array.isArray(row.cells.resultIndicators)) {
            row.cells.resultIndicators = undefined;
            continue;
            // throw new Error(`wrong resultIndicators :: ${JSON.stringify(row.cells.resultIndicators)}`);
          }
          if (row.cells.results) {
            if (typeof row.cells.results !== 'object') {
              throw new Error(`wrong results :: ${JSON.stringify(row.cells.results)}`);
            }
            for (const [resultIndicator, result] of Object.entries(row.cells.results)) {
              if (result) {
                if (results[resultIndicator] === undefined) {
                  results[resultIndicator] = 0;
                }
                results[resultIndicator] += result;
              }
            }
          } else {
            for (const resultIndicator of row.cells.resultIndicators) {
              const result = row.cells[resultIndicator];
              if (result) {
                if (results[resultIndicator] === undefined) {
                  results[resultIndicator] = 0;
                }
                results[resultIndicator] += result;
              }
            }
          }
        }
        cells[fieldName] = results;
      } else if (aggregation.type == 'custom' && ['cvr', 'cpr'].includes(aggregation?.args?.type)) {
        //it will calculate lator
        if (!cells[fieldName]) {
          cells[fieldName] = {};
        }
      } else if (
        aggregation.type == 'custom' &&
        ['impressionsForFrequency', 'reachForFrequency'].includes(aggregation?.args?.type)
      ) {
        const originFieldMapper = {
          impressionsForFrequency: 'impressions',
          reachForFrequency: 'reach',
        };
        if (cells[fieldName] === undefined) {
          cells[fieldName] = 0;
        }
        for (const row of rows) {
          if (row.cells[fieldName]) {
            cells[fieldName] = Number(cells[fieldName]) + Number(row.cells[fieldName]);
          } else {
            if (['facebook', 'kakao'].some(channel => channel === row.channel)) {
              const originFieldName = originFieldMapper[fieldName];
              if (row.cells?.[originFieldName]) {
                cells[fieldName] = Number(cells[fieldName]) + Number(row.cells[originFieldName]);
              }
            }
          }
        }
      } else if (aggregation.type == 'nonAggregable') {
        if (aggregation.ignoreAnalytic || aggregation.ignoreNormal) {
          const nonAnalyticsRows = rows.filter(
            row => row.dataSourceType != 'analytics' || row.dataSourceType != 'normal',
          );
          if (nonAnalyticsRows.length == 1) {
            cells[fieldName] = nonAnalyticsRows[0].cells[fieldName];
          } else {
            cells[fieldName] = null;
          }
        } else {
          if (rows.length == 1) {
            cells[fieldName] = rows[0].cells[fieldName];
          } else {
            cells[fieldName] = null;
          }
        }
      } else if (aggregation.type == 'min') {
        const metrics = [];
        for (const row of rows) {
          if (row.cells?.[fieldName]) {
            metrics.push(row.cells[fieldName]);
          }
        }
        if (metrics.length) {
          cells[fieldName] = _.minBy(metrics);
          // cells[fieldName] = dateFns.format(dateFns.min(...metrics), 'YYYY-MM-DD');
        }
      } else if (aggregation.type == 'max') {
        const metrics = [];
        for (const row of rows) {
          if (row.cells?.[fieldName]) {
            metrics.push(row.cells[fieldName]);
          }
        }
        if (metrics.length) {
          cells[fieldName] = _.maxBy(metrics);
          // cells[fieldName] = dateFns.format(dateFns.max(...metrics), 'YYYY-MM-DD');
        }
      } else if (aggregation.type != 'formula') {
        throw new Error(`Unknown aggregation type: ${aggregation.type}`);
      }
    }
    let childQueries = [];
    for (const row of rows) {
      if (row.childQueries) {
        childQueries.push(...row.childQueries);
      }
      if (isBeforeCalculatedCustomMetricExist) {
        for (const [fieldName, value] of Object.entries(row.cells)) {
          if (fieldName.startsWith('custom-metric:')) {
            if (cells[fieldName] === undefined) {
              cells[fieldName] = 0;
            }
            cells[fieldName] += value;
          }
        }
      }
    }
    childQueries = getCorrectedChildQueries(childQueries);
    const from = moment(moment.min(...(rows.map(row => moment(row.from)) ?? [new Date()]))).format('YYYY-MM-DD');
    const to = moment(moment.max(...(rows.map(row => moment(row.to)) ?? [new Date()]))).format('YYYY-MM-DD');

    const marketingRow = rows.find(row => row.name && row.dataSourceType === 'marketing');
    // we prefer to use the name in the marketing data source
    const name = marketingRow?.name ?? rows[0].name;
    const dataSourceType = marketingRow?.dataSourceType ?? rows[0].dataSourceType;
    aggregatedRows.push({ ...rows[0], name, cells, childQueries, from, to, dataSourceType });
  }
  // Custom metrics and computed metrics
  for (const row of aggregatedRows) {
    for (const header of table.headers) {
      if (header.aggregation?.type == 'formula') {
        const formula = header.aggregation.formula;
        const value = customMetricHelpers.calculate(formula, row.cells);
        if (!isInvalidCellValue(value)) {
          row.cells[header.name] = value;
        }
      }
      if (header.name === 'cvr') {
        if (!row.cells.results || typeof row.cells.results !== 'object') {
          continue;
        }
        const formula = ['Divide', '\\results', '\\impressions'];
        for (const resultIndicator of Object.keys(row.cells.results)) {
          formula[1] = `\\${resultIndicator}`;
          if (!row.cells['cvr']) {
            row.cells['cvr'] = {};
          }
          const value = customMetricHelpers.calculate(formula, row.cells);
          if (!isInvalidCellValue(value)) {
            row.cells['cvr'][resultIndicator] = value;
          }
        }
      }
      if (header.name === 'cpr') {
        if (!row.cells.results || typeof row.cells.results !== 'object') {
          continue;
        }
        const formula = ['Divide', '\\spend', '\\results'];
        for (const resultIndicator of Object.keys(row.cells.results)) {
          formula[2] = `\\${resultIndicator}`;
          if (!row.cells['cvr']) {
            row.cells['cpr'] = {};
          }
          const value = customMetricHelpers.calculate(formula, row.cells);
          if (!isInvalidCellValue(value)) {
            row.cells['cpr'][resultIndicator] = value;
          }
        }
      }
    }
  }
  // Remove null, NaN, undefined, infinity
  for (const row of aggregatedRows) {
    for (const field of Object.keys(row.cells)) {
      const value = row.cells[field];
      if (isInvalidCellValue(value)) {
        delete row.cells[field];
      }
    }
    // it was used in pair with ignoreAnalytics option to ignore analyti rows when aggregate rows but it shouldn't be needed anymore since we had the dataSourceType when we merge
    // if (row.dataSourceType) {
    //   delete row.dataSourceType;
    // }
  }
  let afterCalculatedCustomMetrics = customMetrics;
  if (isUsingCustomMetricV2) {
    afterCalculatedCustomMetrics = newCustomMetricHelpers.getOldFormatCustomMetricsByChannel(
      customMetrics.filter(customMetric => customMetric.details.length === 1),
      null,
    );
  }
  afterCalculatedCustomMetrics = customMetricHelpers.sortCustomMetrics(afterCalculatedCustomMetrics);
  for (const row of aggregatedRows) {
    customMetricHelpers.addCustomMetrics({
      cells: row.cells,
      customMetrics: afterCalculatedCustomMetrics,
      resultIndicator,
    });
    for (const field of Object.keys(row.cells)) {
      const value = row.cells[field];
      if (isInvalidCellValue(value)) {
        delete row.cells[field];
      }
    }
  }
  aggregatedRows = _.sortBy(aggregatedRows, 'from');
  return { ...table, rows: aggregatedRows };
}

function isInvalidCellValue(value) {
  return (typeof value == 'number' && (isNaN(value) || !isFinite(value))) || value == undefined;
}

/**
 * it is needed for GA, AF, Branch to match the correct data sources
 * @param {*} childQueries
 * @returns child queries with the correct parent data sources for the joined queries
 */
function getCorrectedChildQueries(childQueries) {
  const joinedChildQueries = childQueries.filter(childQueries => childQueries.joinedQueries?.length);
  const nonJoinedChildQueries = childQueries.filter(childQueries => !childQueries.joinedQueries);
  if (nonJoinedChildQueries.length === 0) return childQueries;
  const dataSources = nonJoinedChildQueries.flatMap(query => query.dataSources);
  const marketingDataSources = dataSources.filter(dataSource => dataSource.dataSourceType === 'marketing');
  const newJoinedChildQueries = joinedChildQueries.map(query => {
    return {
      ...query,
      dataSources: marketingDataSources,
    };
  });
  return nonJoinedChildQueries.concat(newJoinedChildQueries);
}

module.exports = {
  unionTables,
  aggregateTable,
};
